import { useState } from "react"
import { cn } from "@/lib/utils"
// Relative imports (not the @/components barrel) to avoid an import cycle —
// this file is itself exported from the barrel.
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../Dialog/Dialog"
import { Button } from "../Button/Button"
import { Textarea } from "../Textarea/Textarea"

export interface ChartContextDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Chip label, e.g. "EURUSD · 1h chart". */
  label: string
  /** The staged context text as it would be sent to the AI. */
  text: string
  /** Called with the edited text on Save; the dialog closes itself. */
  onSave?: (text: string) => void
  className?: string
}

const MOBILE_SHEET_CLASSES =
  "max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-xl max-sm:max-h-[88dvh] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]"

// Radix unmounts DialogContent when closed, so the draft state living here is
// created fresh from the staged text on every open — unsaved edits from a
// previous open are discarded without any state-reset effect.
function ContextEditor({
  label,
  text,
  onSave,
  onOpenChange,
}: Pick<ChartContextDialogProps, "label" | "text" | "onSave" | "onOpenChange">) {
  const [draft, setDraft] = useState(text)

  const canSave = draft.trim().length > 0
  const handleSave = () => {
    if (!canSave) return
    onSave?.(draft)
    onOpenChange(false)
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <DialogTitle className="text-section text-foreground">
          Chart context
        </DialogTitle>
        <DialogDescription className="text-body text-muted-foreground mt-1.5">
          {label} — this will be sent to the AI with your next message. You can
          edit it.
        </DialogDescription>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={14}
        aria-label="Chart context text"
        className="font-mono text-mono-sm leading-relaxed max-h-[55dvh] resize-y"
      />
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-label"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSave}
          className="text-label"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

/**
 * Review/edit dialog for the chart context staged on the chat composer.
 * Opened by clicking the composer chip; Save writes the edited text back to
 * the staged attachment (what actually gets sent with the next message).
 */
export function ChartContextDialog({
  open,
  onOpenChange,
  label,
  text,
  onSave,
  className,
}: ChartContextDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "v1 bg-card max-w-[560px] sm:max-w-[560px] w-[94vw] p-6",
          MOBILE_SHEET_CLASSES,
          className,
        )}
      >
        <ContextEditor
          label={label}
          text={text}
          onSave={onSave}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}
